import React, { useState, useMemo } from "react";
import styles from "./TopSellersHero.module.scss";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import BestSellers from "components/Heros/BestSellers/BestSellers";
import { getGameList } from "constants/GameListAdapter";
import BundleGameCard from "components/Heros/BundleCard/BundleCard";
import { addMultipleToCart } from "actions/cart";
import GameInfoModal from "components/Modals/GameInfoModal/GameInfoModal";
import { promoConfig } from "services/promoConfig";

import BundlesUnderline from "images/homepage/BundlesUnderline.png";
import summerSky from "images/heros/Summer Sale/background.jpg";
import summerCity from "images/heros/Summer Sale/CityScape.png";

/**
 * TopSellersHero
 * The homepage "Top Sellers" section, driven by the active promo campaign
 * (services/promoConfig). One data-driven, themeable component that replaces the
 * old BundlesHero (default) + SummerTopSellers (season reskin):
 *  - copy / discount / colours / background / theme come from config
 *  - games: curated list first, then backfilled from a tag (hybrid)
 *  - falls back to <BestSellers/> when disabled or too few games
 * The seeded "default" campaign reproduces the previous BundlesHero look exactly.
 */

// Section label per theme (uppercased in CSS). Default reproduces "TOP SELLERS".
const THEME_LABELS = {
    default: "Top Sellers",
    summer: "Summer Sale",
    halloween: "Halloween Sale",
    christmas: "Christmas Sale",
};

const TopSellersHero = () => {
    const cfg = promoConfig.getConfig();
    const [modalGame, setModalGame] = useState(null);
    const [activeTabIdx, setActiveTabIdx] = useState(0);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const theme = cfg.theme || "default";
    const discountPercent = cfg.discountPercent ?? 30;
    const minGames = cfg.minGames ?? 3;
    const maxGames = cfg.maxGames ?? 4;
    const filterTag = cfg.filterTag || "best-seller";

    // Configurable tabs (ordered { label, tag }). Empty = single combined list.
    const tabs = Array.isArray(cfg.tabs) ? cfg.tabs : [];

    // Hybrid game selection: curated (ordered) ids first, then backfill from the
    // tag for any not already curated, then filter by the active tab and cap at maxGames.
    const tabGames = useMemo(() => {
        const all = getGameList().filter(g => g.status === "enabled");

        let pool;
        if (cfg.gameIds?.length) {
            const byId = new Map(all.map(g => [g.gameId, g]));
            const curated = cfg.gameIds.map(id => byId.get(id)).filter(Boolean);
            const curatedIds = new Set(curated.map(g => g.gameId));
            const backfill = all.filter(g => g.tags?.includes(filterTag) && !curatedIds.has(g.gameId));
            pool = [...curated, ...backfill];
        } else {
            pool = all.filter(g => g.tags?.includes(filterTag));
        }

        const activeTab = tabs[activeTabIdx];
        const filtered = activeTab ? pool.filter(g => g.tags?.includes(activeTab.tag)) : pool;
        return filtered.slice(0, maxGames);
    }, [activeTabIdx, cfg, filterTag, maxGames, tabs]);

    const handleAddToCart = (games) => {
        if (games.length) {
            dispatch(addMultipleToCart(games));
        }
    };

    if (cfg.enabled === false) return <BestSellers />;
    if (tabGames.length < 2) return <BestSellers />;

    const label = THEME_LABELS[theme] || THEME_LABELS.default;
    const title = cfg.title || `Grab all our top sellers get ${discountPercent}% off`;
    const subtitle = cfg.subtitle || `Buy ${minGames} or more games and save automatically`;

    // Background: admin image wins; the summer theme ships a bundled sky as a default.
    const bgImage = cfg.backgroundImageUrl || (theme === "summer" ? summerSky : null);
    const showCityStrip = theme === "summer";

    const themeClass = theme !== "default" && styles[theme] ? styles[theme] : "";
    const styleVars = {
        "--promo-bg": cfg.backgroundColour || undefined,
        "--promo-text": cfg.textColour || undefined,
        "--promo-accent": cfg.accentColour || undefined,
    };

    return (
        <div className={`${styles.container} ${themeClass}`} style={styleVars}>
            {/* Themed dressing */}
            {bgImage && <img src={bgImage} className={styles.bgImage} alt="" aria-hidden="true" />}
            {showCityStrip && <img src={summerCity} className={styles.cityStrip} alt="" aria-hidden="true" />}

            <div className={styles.content}>
                {/* Header */}
                <div className={styles.headerArea}>
                    <div className={styles.sectionLabel}>
                        <span className={styles.labelText}>{label}</span>
                        {(theme === "default" || theme === "summer") && (
                            <img src={BundlesUnderline} alt="" className={styles.labelUnderline} />
                        )}
                    </div>
                    <div className={styles.title}>{title}</div>
                    <p className={styles.subtitle}>{subtitle}</p>
                </div>

                {/* Tabs (hidden when the campaign defines none → single combined list) */}
                {tabs.length > 0 && (
                    <div className={styles.tabBar}>
                        {tabs.map((tab, i) => (
                            <button
                                key={i}
                                className={`${styles.tab} ${activeTabIdx === i ? styles.tabActive : ''}`}
                                onClick={() => setActiveTabIdx(i)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                )}

                {/* Game cards */}
                <div className={styles.bundleContent}>
                    {/* Desktop: game cards in a row */}
                    <div className={styles.gameRow}>
                        {tabGames.map(game => (
                            <div key={game.gameId} className={styles.gameSlot}>
                                <BundleGameCard
                                    game={game}
                                    noButton={true}
                                    onClick={() => setModalGame(game)}
                                    selected={false}
                                    isActive={false}
                                    onLearnMore={(e) => { e.stopPropagation(); setModalGame(game); }}
                                    showInfoButton={true}
                                    compact={true}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Mobile: compact 2-col grid */}
                    <div className={styles.mobileGameGrid}>
                        {tabGames.map(game => (
                            <div key={game.gameId} className={styles.mobileGameSlot}>
                                <BundleGameCard
                                    game={game}
                                    noButton={true}
                                    onClick={() => setModalGame(game)}
                                    selected={false}
                                    isActive={false}
                                    onLearnMore={(e) => { e.stopPropagation(); setModalGame(game); }}
                                    showInfoButton={true}
                                    compact={true}
                                />
                            </div>
                        ))}
                    </div>

                    {/* CTA Buttons */}
                    <div className={styles.bundleCta}>
                        <button
                            className={styles.addButton}
                            onClick={() => handleAddToCart(tabGames)}
                        >
                            ADD TO BASKET
                        </button>

                        <button type="button" className={styles.shopAllButton} onClick={() => navigate("/shop")}>
                            Shop all games
                        </button>
                    </div>
                </div>
            </div>

            {/* Game Info Modal */}
            <GameInfoModal
                show={!!modalGame}
                game={modalGame}
                onClose={() => setModalGame(null)}
            />
        </div>
    );
};

export default TopSellersHero;
